import { Random } from '@framework/Random';
import { ModelCommentEntity } from './ModelCommentEntity';
import type { Timestamp } from '@framework/Timestamp';
import type {
  ModelCommentThreadJSON,
  ModelCommentJSON,
  ModelCommentId,
  ModelCommentThreadId,
  UserId,
} from 'balus-common';

export class ModelCommentThread {
  private readonly replyComments: ModelCommentEntity[];
  constructor(
    public readonly id: ModelCommentThreadId,
    private readonly status: ModelCommentThreadJSON['status'],
    private readonly primaryComment: ModelCommentEntity,
    replyComments: ModelCommentEntity[],
    private readonly readByUserIds?: UserId[]
  ) {
    // 投稿日付順に並び替える
    this.replyComments = [...replyComments].sort((a, b) => a.compareByPostAt(b));
    this.readByUserIds = readByUserIds ? [...readByUserIds] : undefined;
  }

  static buildNewId(): ModelCommentThreadId {
    return Random.generateRandomID(20, 'MCT');
  }

  static load(dump: ModelCommentThreadJSON): ModelCommentThread {
    const { id, status, primaryComment, replyComments, readByUserIds } = dump;
    return new this(
      id,
      status,
      ModelCommentEntity.load(primaryComment),
      (replyComments || []).map((comment) => ModelCommentEntity.load(comment)),
      readByUserIds
    );
  }

  dump(): ModelCommentThreadJSON {
    const { id, status, primaryComment, replyComments, readByUserIds } = this;
    return {
      id,
      status,
      primaryComment: primaryComment.dump(),
      ...(readByUserIds && readByUserIds.length > 0 ? { readByUserIds: [...readByUserIds] } : {}),
      replyComments: replyComments.map((comment) => comment.dump()),
    };
  }

  cloneNew(): ModelCommentThread {
    return new ModelCommentThread(
      ModelCommentThread.buildNewId(),
      this.status,
      this.primaryComment.cloneNew(),
      this.replyComments.map((comment) => comment.cloneNew()),
      this.readByUserIds ? [...this.readByUserIds] : undefined
    );
  }

  isEqual(other: ModelCommentThread): boolean {
    const sameReadByUserIds =
      (this.readByUserIds ?? []).length === (other.readByUserIds ?? []).length &&
      (this.readByUserIds ?? []).every((id) => (other.readByUserIds ?? []).includes(id));

    return (
      this.id === other.id &&
      this.status === other.status &&
      this.primaryComment.isEqual(other.primaryComment) &&
      this.replyComments.length === other.replyComments.length &&
      this.replyComments.every((reply) => {
        const otherReply = other.getReplyComment(reply.id);
        return otherReply && reply.isEqual(otherReply);
      }) &&
      sameReadByUserIds
    );
  }

  isReadBy(userId: UserId): boolean {
    // readByUserIds が undefined の場合は readByUserId 管理導入前の古いスレッドとみなし既読扱いにする。
    // undefined を未読（空配列）と同一視すると、既存スレッド全件が未読表示になりノイジーになるため。
    // 空配列 [] は「管理開始後、まだ誰も読んでいない」を意味し、こちらは正しく未読扱いとなる。
    if (this.readByUserIds === undefined) return true;
    return this.readByUserIds.includes(userId);
  }

  isOpen(): boolean {
    return this.status === 'Open';
  }

  isResolved(): boolean {
    return this.status === 'Resolved';
  }

  getPrimaryComment(): ModelCommentEntity {
    return this.primaryComment;
  }

  getReplyComments(): ModelCommentEntity[] {
    return [...this.replyComments];
  }

  getRegularReplyComments(): ModelCommentEntity[] {
    return this.replyComments.filter((reversedReplyComment) => {
      return reversedReplyComment.isRegularComment();
    });
  }

  addReplyComment(userId: UserId, body: string, type?: ModelCommentJSON['type']): ModelCommentThread {
    const { id, status, primaryComment, replyComments } = this;
    const newComment = ModelCommentEntity.buildNew(userId, body, type);
    return new ModelCommentThread(id, status, primaryComment, [...replyComments, newComment], [userId]);
  }

  removeReplyComment(commentId: ModelCommentId): ModelCommentThread {
    return ModelCommentThread.load({
      ...this.dump(),
      replyComments: this.replyComments.filter(({ id }) => id !== commentId).map((comment) => comment.dump()),
    });
  }

  getReplyComment(commentId: ModelCommentId): ModelCommentEntity | undefined {
    return this.replyComments.find(({ id }) => id === commentId);
  }

  setReadByUserId(userId: UserId): ModelCommentThread {
    const { id, status, primaryComment, replyComments, readByUserIds } = this;
    const currentIds = readByUserIds ?? [];
    // 既に既読済みの場合はそのまま返す（重複追加防止）
    if (currentIds.includes(userId)) return this;
    return new ModelCommentThread(id, status, primaryComment, replyComments, [...currentIds, userId]);
  }

  withStatus(status: ModelCommentThreadJSON['status']): ModelCommentThread {
    const { id, primaryComment, replyComments, readByUserIds } = this;
    return new ModelCommentThread(id, status, primaryComment, replyComments, readByUserIds);
  }

  withCommentBody(commentId: ModelCommentId, body: string): ModelCommentThread {
    const { primaryComment, replyComments } = this;

    if (primaryComment.id === commentId) {
      return ModelCommentThread.load({
        ...this.dump(),
        primaryComment: primaryComment.withBody(body).dump(),
      });
    }

    return ModelCommentThread.load({
      ...this.dump(),
      replyComments: replyComments
        .map((comment) => (comment.id === commentId ? comment.withBody(body) : comment))
        .map((comment) => comment.dump()),
    });
  }

  compareByLastPostAt(otherThread: ModelCommentThread): number {
    return this.getLastPostAt().compareTo(otherThread.getLastPostAt());
  }

  getLastPostAt(): Timestamp {
    // 返信コメントが1件以上あれば、その最後の要素の投稿日時を返す (返信コメントはコンストラクタで並び替え済み)
    if (this.replyComments.length > 0) {
      return this.replyComments[this.replyComments.length - 1].getPostAt();
    }

    return this.primaryComment.getPostAt();
  }
}
